import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import campaignImage from "../../assets/cta-campaign-1376.webp";
import campaignImageSmall from "../../assets/cta-campaign-800.webp";

// --- REUSABLE LUXURY BUTTON ---
const LuxuryButton = ({ to, children, variant = 'white' }) => (
  <Link to={to}>
    <button className={`
      group relative px-10 py-4 overflow-hidden transition-all duration-300
      ${variant === 'white' 
        ? 'bg-white text-black hover:bg-gray-100' 
        : 'bg-black text-white hover:bg-gray-900'}
    `}>
      <span className="relative z-10 text-[11px] uppercase tracking-[0.2em] font-medium">
        {children}
      </span>
    </button>
  </Link>
);

function Cta() {
  const [isVisible, setIsVisible] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const sectionRef = useRef(null);
  
  const homepage = useSelector((state) => state.homepage.homepage || []);
  const ctaData = homepage.find(item => item.sectionType === "cta") || {};
  
  // The API image only arrives after the homepage fetch resolves, so this local
  // asset is what every visitor sees first - it ships hashed and cached rather
  // than costing a third-party request on the critical path.
  const imageUrl = ctaData.imageUrl || campaignImage;
  const isLocalCampaign = imageUrl === campaignImage;

  // UPDATED DEFAULTS: Focus on Product/Collection, not Blog
  const title = ctaData.title || "Signature Scent";
  const subtitle = ctaData.subtitle || "Experience the essence of modern luxury. Defined by rarity, crafted for the individual.";
  const buttonText = ctaData.buttonText || "Shop the Collection";
  const buttonLink = ctaData.buttonLink || "/shop";

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section 
      ref={sectionRef} 
      className="w-full py-24 md:py-32 px-4 md:px-8 lg:px-12 bg-white"
    >
      {/*
        The campaign image is a 16:9 product lineup. A 600px-tall box on a phone
        crops away most of the row, so the small-screen height is reduced to keep
        more of the lineup in frame.
      */}
      <div className="relative w-full max-w-[1440px] mx-auto h-[440px] md:h-[700px] overflow-hidden bg-gray-100">
        
        {/* --- BACKGROUND IMAGE --- */}
        <div className="absolute inset-0">
          <img
            src={imageUrl}
            srcSet={isLocalCampaign ? `${campaignImageSmall} 800w, ${campaignImage} 1376w` : undefined}
            sizes={isLocalCampaign ? '(max-width: 1440px) 100vw, 1440px' : undefined}
            alt="Editorial Campaign"
            loading="lazy"
            decoding="async"
            onLoad={() => setImageLoaded(true)}
            className={`
              w-full h-full object-cover transition-all duration-[1.5s] ease-out
              ${isVisible ? 'scale-100 opacity-100' : 'scale-110 opacity-0'}
            `}
          />

          {/*
            Cinematic Overlay. The campaign shot is a product lineup on white
            marble, so a 20% wash leaves white text unreadable against it - it
            needs a real scrim, weighted toward the centre band where the copy
            sits. API-supplied images are darker already and only need a light touch.
          */}
          {isLocalCampaign ? (
            <>
              <div className="absolute inset-0 bg-black/35" />
              {/* Concentrated behind the copy, so the bottles at the edges stay lit */}
              <div
                className="absolute inset-0"
                style={{
                  background:
                    'radial-gradient(ellipse 65% 60% at 50% 50%, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.3) 60%, rgba(0,0,0,0) 100%)'
                }}
              />
            </>
          ) : (
            <div className="absolute inset-0 bg-black/20" />
          )}
        </div>

        {/* --- CONTENT OVERLAY --- */}
        <div className={`
          absolute inset-0 flex flex-col items-center justify-center text-center px-6
          transition-all duration-1000 delay-300
          ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}
        `}>
          
          <span className="inline-block text-[10px] md:text-xs uppercase tracking-[0.3em] text-white/80 mb-6 border border-white/30 px-4 py-2 backdrop-blur-sm">
            Campaign
          </span>

          <h2 className="text-5xl md:text-7xl lg:text-8xl font-serif italic text-white mb-6 leading-none drop-shadow-lg">
            {title}
          </h2>

          <p className="text-sm md:text-base font-light text-white/90 max-w-lg mb-10 leading-relaxed drop-shadow-md">
            {subtitle}
          </p>

          <LuxuryButton to={buttonLink} variant="white">
            {buttonText}
          </LuxuryButton>

        </div>

      </div>
    </section>
  );
}

export default Cta;